/**
 * ENCAPTURE — PRODUCT THUMBNAILS ↔ VARIANT SYNC (STABLE v2)
 * File: assets/product-thumbnails.js
 *
 * Obiettivi:
 * - Click thumbnail => seleziona slide GIUSTA (no “immagine precedente”)
 * - Click thumbnail variante => aggiorna swatch/variante (così ATC prende quella giusta)
 * - Ripristina bordo/quadrato selezione thumbnail
 * - Anti-conflitto con handler vecchi: capture + stopImmediatePropagation
 * - (Opzionale) rimuove doppioni: 1 thumbnail per variante (solo thumbs di variante)
 *
 * Richiede in product.thumbnails.liquid:
 * - data-media-id="{{ section.id }}-{{ media.id }}"
 * - data-variant-id="{{ related_variant.id }}" (quando esiste)
 */

if (!customElements.get('product-slider-thumbnails')) {
  class ProductSliderThumbnails extends HTMLElement {
    constructor() {
      super();

      // bind sicuri
      this.setupProductGallery = this.setupProductGallery.bind(this);
      this._onThumbClickDelegated = this._onThumbClickDelegated.bind(this);

      // quando cambiano varianti/DOM, riaggancia zoom gallery
      this.addEventListener('change', this.setupProductGallery);

      // mappe (slide)
      this._cellIndexByRaw = new Map();
      this._cellIndexByNum = new Map();
    }

    connectedCallback() {
      this.product_container = this.closest('.thb-product-detail');
      if (!this.product_container) return;

      this.thumbnail_container = this.product_container.querySelector('.product-thumbnail-container');
      this.video_containers = this.querySelectorAll('.product-single__media-external-video--play');

      this.setOptions();
      this.init();
    }

    setOptions() {
      this.hide_variants = this.dataset.hideVariants == 'true';

      this.thumbnails = this.thumbnail_container
        ? this.thumbnail_container.querySelectorAll('.product-thumbnail')
        : [];

      this.prev_button = this.querySelector('.flickity-prev');
      this.next_button = this.querySelector('.flickity-next');

      this.options = {
        draggable: false,
        wrapAround: true,
        pageDots: false,
        contain: true,
        adaptiveHeight: false,
        initialIndex: '.is-initial-selected',
        prevNextButtons: false,
        fade: false,
        cellSelector: '.product-images__slide.is-active'
      };
    }

    init() {
      if (typeof Flickity === 'undefined') return;

      this.flkty = new Flickity(this, this.options);
      this.selectedIndex = this.flkty.selectedIndex || 0;

      this._refreshCellMaps();
      this._setupEvents();
      this._bindThumbDelegation();
      this._dedupeVariantThumbs(); // opzionale: 1 thumb per variante
      this.setupProductGallery();

      setTimeout(() => this._safeResize(), 300);
    }

    reInit() {
      if (!this.flkty) return;

      try { this.flkty.destroy(); } catch (e) {}

      this.setOptions();
      this.flkty = new Flickity(this, this.options);
      this.selectedIndex = this.flkty.selectedIndex || 0;

      this._refreshCellMaps();
      this._setupEvents();
      this._bindThumbDelegation();
      this._dedupeVariantThumbs();
      this.setupProductGallery();

      setTimeout(() => this._safeResize(), 150);
    }

    /* -------------------- HELPERS (MEDIA / VARIANT / FORM) -------------------- */

    _extractRawId(el) {
      if (!el) return '';
      // ✅ ora preferiamo data-media-id (quello aggiunto in Liquid)
      return (
        el.getAttribute?.('data-media-id') ||
        el.dataset?.mediaId ||
        el.id ||
        ''
      );
    }

    _extractNumericId(raw) {
      const s = String(raw || '');
      const m = s.match(/(\d+)(?!.*\d)/); // ultime cifre nel testo
      return m ? m[1] : '';
    }

    // ✅ ora preferiamo data-variant-id (aggiunto in Liquid)
    _extractVariantIdFromThumb(thumbEl) {
      if (!thumbEl) return '';

      const d1 = thumbEl.getAttribute?.('data-variant-id') || thumbEl.dataset?.variantId;
      if (d1) return String(d1);

      // fallback vecchio (se in qualche template esiste ancora)
      const id = String(thumbEl.id || '');
      const m = id.match(/main-product-(\d+)/);
      if (m && m[1]) return m[1];

      return '';
    }

    _getVariantSelects() {
      return this.product_container?.querySelector('variant-selects') || null;
    }

    _getVariantData(vs) {
      try {
        if (!vs) return null;
        const json = vs.querySelector('[type="application/json"]');
        if (!json) return null;
        vs.variantData = vs.variantData || JSON.parse(json.textContent);
        return vs.variantData;
      } catch (e) {
        return null;
      }
    }

    // ✅ HARD SYNC: se cambia la variante, ATC deve usare quella giusta
    _updateAddToCartVariantId(variantId) {
      if (!variantId) return;

      const form =
        this.product_container?.querySelector('form[action*="/cart/add"]') ||
        this.product_container?.querySelector('form[data-type="add-to-cart-form"]') ||
        this.product_container?.querySelector('form');

      const input = form?.querySelector('input[name="id"]');
      if (!input) return;

      input.value = String(variantId);
      input.dispatchEvent(new Event('input', { bubbles: true }));
      input.dispatchEvent(new Event('change', { bubbles: true }));
    }

    _refreshCellMaps() {
      this._cellIndexByRaw.clear();
      this._cellIndexByNum.clear();

      if (!this.flkty?.cells?.length) return;

      this.flkty.cells.forEach((cell, idx) => {
        const el = cell.element;
        const raw = this._extractRawId(el);
        const num = this._extractNumericId(raw);

        if (raw) {
          this._cellIndexByRaw.set(raw, idx);

          // spesso formato "SECTION-MEDIA": salva anche ultimo segmento
          if (raw.includes('-')) {
            const last = raw.split('-').pop();
            if (last) this._cellIndexByRaw.set(last, idx);
          }
        }
        if (num) this._cellIndexByNum.set(num, idx);
      });
    }

    _getCellIndexForThumb(thumbEl) {
      this._refreshCellMaps();

      const raw = this._extractRawId(thumbEl);
      const num = this._extractNumericId(raw);

      if (raw && this._cellIndexByRaw.has(raw)) return this._cellIndexByRaw.get(raw);

      if (raw && raw.includes('-')) {
        const last = raw.split('-').pop();
        if (last && this._cellIndexByRaw.has(last)) return this._cellIndexByRaw.get(last);
      }

      if (num && this._cellIndexByNum.has(num)) return this._cellIndexByNum.get(num);

      const visible = Array.from(this.thumbnails).filter(el => el.clientWidth > 0 && !el.hidden);
      const idx = visible.indexOf(thumbEl);
      return idx >= 0 ? idx : null;
    }

    /* -------------------- THUMB UI -------------------- */

    _setThumbSelected(thumbEl) {
      if (!thumbEl) return;

      Array.from(this.thumbnails).forEach(t => {
        t.classList.remove('is-initial-selected');
        t.classList.remove('is-selected');
      });

      thumbEl.classList.add('is-initial-selected');
      thumbEl.classList.add('is-selected');

      requestAnimationFrame(() => {
        if (!thumbEl.offsetParent) return;

        const windowHalfHeight = thumbEl.offsetParent.clientHeight / 2;
        const windowHalfWidth = thumbEl.offsetParent.clientWidth / 2;

        thumbEl.parentElement?.scrollTo({
          left: thumbEl.offsetLeft - windowHalfWidth + thumbEl.clientWidth / 2,
          top: thumbEl.offsetTop - windowHalfHeight + thumbEl.clientHeight / 2,
          behavior: 'smooth'
        });
      });
    }

    _safeResize() {
      try { this.flkty?.resize(); } catch (e) {}
      try { this.flkty?.reposition(); } catch (e) {}
    }

    // ✅ opzionale: 1 thumbnail per variante (solo thumbs che hanno data-variant-id)
    _dedupeVariantThumbs() {
      const seen = new Set();

      Array.from(this.thumbnails).forEach(t => {
        if (t.dataset._dup === '1') {
          t.hidden = false;
          t.dataset._dup = '0';
        }
      });

      Array.from(this.thumbnails).forEach(t => {
        const vId = this._extractVariantIdFromThumb(t);
        if (!vId) return;

        if (seen.has(vId)) {
          t.hidden = true;
          t.dataset._dup = '1';
        } else {
          seen.add(vId);
          t.dataset._dup = '0';
        }
      });
    }

    /* -------------------- CLICK DELEGATO (ANTI-CONFLITTO) -------------------- */

    _bindThumbDelegation() {
      if (!this.thumbnail_container) return;

      if (this.thumbnail_container.dataset.thumbDelegationBound === '1') return;
      this.thumbnail_container.dataset.thumbDelegationBound = '1';

      // capture + stopImmediatePropagation => blocca handler vecchi che causavano “immagine precedente”
      this.thumbnail_container.addEventListener('click', this._onThumbClickDelegated, true);
    }

    _onThumbClickDelegated(e) {
      const thumb = e.target.closest?.('.product-thumbnail');
      if (!thumb || !this.thumbnail_container.contains(thumb)) return;

      e.preventDefault();
      e.stopImmediatePropagation();

      this.thumbnailClick(thumb);
    }

    /* -------------------- FLICKITY EVENTS -------------------- */

    _setupEvents() {
      // Prev/Next (cloni per evitare doppio bind del tema)
      if (this.prev_button) {
        const prev = this.prev_button.cloneNode(true);
        this.prev_button.parentNode.append(prev);
        this.prev_button.remove();

        prev.addEventListener('click', () => this.flkty.previous());
        prev.addEventListener('keyup', (event) => {
          this.flkty.previous();
          event.preventDefault();
        });
      }

      if (this.next_button) {
        const next = this.next_button.cloneNode(true);
        this.next_button.parentNode.append(next);
        this.next_button.remove();

        next.addEventListener('click', () => this.flkty.next());
        next.addEventListener('keyup', (event) => {
          this.flkty.next();
          event.preventDefault();
        });
      }

      // Video overlay play
      this.video_containers.forEach((container) => {
        const btn = container.querySelector('button');
        if (!btn) return;
        btn.addEventListener('click', function () {
          container.setAttribute('hidden', '');
        });
      });

      // aggiorna selectedIndex
      this.flkty.on('settle', (index) => {
        this.selectedIndex = index;
      });

      // change slide => aggiorna thumb highlight + stop video precedente
      this.flkty.on('change', (index) => {
        const prevCell = this.flkty.cells?.[this.selectedIndex];
        const previous_slide = prevCell ? prevCell.element : null;
        const previous_media = previous_slide ? previous_slide.querySelector('.product-single__media') : null;

        const currentCell = this.flkty.cells?.[index]?.element;
        const cellRaw = this._extractRawId(currentCell);
        const cellNum = this._extractNumericId(cellRaw);

        const thumbs = Array.from(this.thumbnails).filter(el => el.clientWidth > 0 && !el.hidden);
        let matchedThumb = null;

        if (cellRaw) {
          matchedThumb = thumbs.find(t => this._extractRawId(t) === cellRaw) || null;
          if (!matchedThumb && cellRaw.includes('-')) {
            const last = cellRaw.split('-').pop();
            matchedThumb = thumbs.find(t => this._extractRawId(t) === last) || null;
          }
        }
        if (!matchedThumb && cellNum) {
          matchedThumb = thumbs.find(t => this._extractNumericId(this._extractRawId(t)) === cellNum) || null;
        }
        if (!matchedThumb) matchedThumb = thumbs[index] || thumbs[0] || null;

        if (matchedThumb) this._setThumbSelected(matchedThumb);

        // Stop previous video
        if (previous_media && previous_media.classList.contains('product-single__media-external-video')) {
          if (previous_media.dataset.provider === 'youtube') {
            previous_media.querySelector('iframe')?.contentWindow?.postMessage(JSON.stringify({
              event: "command", func: "pauseVideo", args: ""
            }), "*");
          } else if (previous_media.dataset.provider === 'vimeo') {
            previous_media.querySelector('iframe')?.contentWindow?.postMessage(JSON.stringify({
              method: "pause"
            }), "*");
          }
          previous_media.querySelector('.product-single__media-external-video--play')?.removeAttribute('hidden');
        } else if (previous_media && previous_media.classList.contains('product-single__media-native-video')) {
          previous_media.querySelector('video')?.pause();
        }
      });
    }

    /* -------------------- CORE: THUMB → SLIDE + VARIANT/SWATCH -------------------- */

    thumbnailClick(thumbnail) {
      if (!this.flkty) return;

      // 1) UI selezione thumb subito
      this._setThumbSelected(thumbnail);

      // 2) seleziona slide corretta (sempre)
      const idx = this._getCellIndexForThumb(thumbnail);
      if (typeof idx === 'number') {
        this.flkty.select(idx, false, true);
      }
      this._safeResize();

      // 3) ✅ SYNC VARIANTE:
      //    - prima tramite data-variant-id (affidabile)
      //    - fallback tramite featured_media.id (se 1:1)
      const variantId = this._extractVariantIdFromThumb(thumbnail);
      if (variantId) {
        this._syncVariantFromVariantId(variantId);
        return;
      }

      const thumbRaw = this._extractRawId(thumbnail);
      const thumbNum = this._extractNumericId(thumbRaw);
      if (thumbNum) this._syncVariantFromMediaId(thumbNum);
    }

    _syncVariantFromVariantId(variantId) {
      try {
        // ✅ hard sync per ATC SEMPRE corretto
        this._updateAddToCartVariantId(variantId);

        const vs = this._getVariantSelects();
        const variantData = this._getVariantData(vs);
        if (!vs || !variantData) return;

        const target = variantData.find(v => String(v?.id) === String(variantId));
        if (!target) return;

        this._applyVariantOptionsToUI(vs, target);

        // ✅ trigger finale: attiva VariantSelects.onVariantChange()
        vs.dispatchEvent(new Event('change', { bubbles: true }));
        if (typeof vs.onVariantChange === 'function') {
          // extra-safe (non fa male)
          try { vs.onVariantChange(); } catch (e) {}
        }
      } catch (e) {}
    }

    _syncVariantFromMediaId(mediaIdNum) {
      try {
        const vs = this._getVariantSelects();
        const variantData = this._getVariantData(vs);
        if (!vs || !variantData) return;

        const target = variantData.find(v => String(v?.featured_media?.id) === String(mediaIdNum));
        if (!target) return;

        // ✅ hard sync per ATC corretto
        this._updateAddToCartVariantId(target.id);

        this._applyVariantOptionsToUI(vs, target);

        vs.dispatchEvent(new Event('change', { bubbles: true }));
        if (typeof vs.onVariantChange === 'function') {
          try { vs.onVariantChange(); } catch (e) {}
        }
      } catch (e) {}
    }

    _applyVariantOptionsToUI(vs, targetVariant) {
      (targetVariant.options || []).forEach((opt, i) => {
        const pos = i + 1;

        const fieldset =
          vs.querySelector(`fieldset[data-option-position="${pos}"]`) ||
          vs.querySelectorAll('fieldset')[i] ||
          null;

        // 1) radio/swatches
        if (fieldset) {
          const inputs = Array.from(fieldset.querySelectorAll('input[type="radio"], input'));
          const hit = inputs.find(inp => inp.value === opt || inp.dataset?.value === opt);
          if (hit) {
            hit.checked = true;
            // ✅ alcuni temi ascoltano proprio input/change sul radio
            hit.dispatchEvent(new Event('input', { bubbles: true }));
            hit.dispatchEvent(new Event('change', { bubbles: true }));
          }

          // 2) select dentro fieldset (se esiste)
          const sel = fieldset.querySelector('select');
          if (sel) {
            sel.value = opt;
            sel.dispatchEvent(new Event('input', { bubbles: true }));
            sel.dispatchEvent(new Event('change', { bubbles: true }));
          }
        }

        // 3) select fallback globale
        const select =
          vs.querySelector(`select[name="options[${i}]"]`) ||
          vs.querySelectorAll('select')[i];

        if (select) {
          select.value = opt;
          select.dispatchEvent(new Event('input', { bubbles: true }));
          select.dispatchEvent(new Event('change', { bubbles: true }));
        }
      });
    }

    /* -------------------- ZOOM (Photoswipe) -------------------- */

    setDraggable(draggable) {
      if (!this.flkty) return;
      this.flkty.options.draggable = draggable;
      this.flkty.updateDraggable();
    }

    selectCell(mediaId) {
      if (!this.flkty) return;
      this.flkty.selectCell(mediaId);
    }

    setupProductGallery() {
      if (!this.querySelectorAll('.product-single__media-zoom').length) return;
      this.setEventListeners();
    }

    buildItems() {
      this.activeImages = Array.from(
        this.querySelectorAll('.product-images__slide.is-active .product-single__media-image')
      );

      return this.activeImages.map((item) => {
        let index = [].indexOf.call(item.parentNode.parentNode.children, item.parentNode);
        let activelink = item.querySelector('.product-single__media-zoom');
        if (activelink) activelink.dataset.index = index;

        return {
          src: activelink?.getAttribute('href'),
          msrc: activelink?.dataset?.msrc,
          w: activelink?.dataset?.w,
          h: activelink?.dataset?.h
        };
      });
    }

    setEventListeners() {
      this.links = this.querySelectorAll('.product-single__media-zoom');
      this.pswpElement = document.querySelectorAll('.pswp')[0];
      if (!this.pswpElement) return;

      this.pswpOptions = {
        maxSpreadZoom: 2,
        loop: false,
        allowPanToNext: false,
        closeOnScroll: false,
        showHideOpacity: false,
        arrowKeys: true,
        history: false,
        captionEl: false,
        fullscreenEl: false,
        zoomEl: false,
        shareEl: false,
        counterEl: true,
        arrowEl: true,
        preloaderEl: true,
        getThumbBoundsFn: () => {
          const thumbnail = this.querySelector('.product-images__slide.is-selected');
          const pageYScroll = window.pageYOffset || document.documentElement.scrollTop;
          const rect = thumbnail.getBoundingClientRect();
          return { x: rect.left, y: rect.top + pageYScroll, w: rect.width };
        }
      };

      this.links.forEach((link) => {
        if (link.dataset.zoomBound === '1') return;
        link.dataset.zoomBound = '1';
        link.addEventListener('click', (e) => this.zoomClick(e, link));
      });
    }

    zoomClick(e, link) {
      this.items = this.buildItems();
      this.pswpOptions.index = parseInt(link.dataset.index, 10);

      if (typeof PhotoSwipe !== 'undefined') {
        let pswp = new PhotoSwipe(this.pswpElement, PhotoSwipeUI_Default, this.items, this.pswpOptions);
        pswp.listen('firstUpdate', function () {
          pswp.listen('parseVerticalMargin', function (item) {
            item.vGap = { top: 50, bottom: 50 };
          });
        });
        pswp.init();
      }

      e.preventDefault();
    }
  }

  customElements.define('product-slider-thumbnails', ProductSliderThumbnails);
}
